import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "@/hooks/useDeskNavigate";
import useGoBack from "@/hooks/useGoBack";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, Pencil, Trash2, ArrowLeft, FileText, ArrowRight, Shuffle, Search, Users, AlertTriangle, RotateCw, Copy, Zap, ChevronDown, Globe, CheckCircle2 } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "@/lib/toast";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { avatarColorForSeed } from "@/lib/avatarColors";
import UserAvatar from "@/components/ui/UserAvatar";
import DocumentTypeDialog from "@/components/documents/DocumentTypeDialog";
import DocumentTypeResponsablesPopover from "@/components/documents/DocumentTypeResponsablesPopover";
import DuplicateTypeDialog from "@/components/documents/DuplicateTypeDialog";
import DeleteTypeDialog from "@/components/documents/DeleteTypeDialog";
import RestrictionDialog from "@/components/documents/RestrictionDialog";
// Spec iter164s (2026-02-25) — Bouton et modale « Nettoyer les
// propagations parasites » RÉTABLIS avec l'endpoint enrichi
// (heuristique par taille, expected_step_count). L'admin déclenche
// le diagnostic et la correction depuis l'UI, sans jamais toucher un
// terminal ni un token.
import CleanupWrongPropagationDialog from "@/components/documents/CleanupWrongPropagationDialog";
import RestorePivotDialog from "@/components/documents/RestorePivotDialog";
import { LockIcon as LockIcon, ShieldCheck } from "lucide-react";
import CategoriesManagerPanel from "@/components/documents/CategoriesManagerPanel";
import { AvatarBlock } from "@/components/settings/AccountSection";
import ColumnHeaderMenu from "@/components/ui/ColumnHeaderMenu";
import SearchClearButton from "@/components/ui/SearchClearButton";
import HeaderProfileGroup from "@/components/TopBar";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";

/**
 * DocumentTypesAdmin — CRUD interface for Document Types.
 * Spec 2026-02-XX rev29 — L'édition inline du nom court a été retirée
 * (crayon rapide sur le badge, regen, save/cancel). L'édition passe
 * désormais UNIQUEMENT par le dialog complet accessible via la colonne
 * Actions. La colonne « Nom complet » a également été retirée : le badge
 * (colonne unique « Type ») affiche le nom complet du type, entier, sans
 * troncature ni ellipsis.
 */

const PILL = { display: "inline-flex", alignItems: "center", gap: 5, borderRadius: 14, padding: "4px 11px", fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" };

/** Colonne « Accès » (spec iter146 P1) : Public · pile d'avatars des autorisés (UserAvatar, max 3 + « +N ») · alerte si personne. */
function AccessCell({ type: t, users, onOpenList }) {
  const [pop, setPop] = useState(false);
  if (!t.restriction_active) {
    return (
      <div className="flex items-center" style={{ gap: 9 }} data-testid={`doctype-access-pill-${t.id}`} data-state="public">
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5, borderRadius: 100, padding: "3px 10px", fontSize: 10.5, fontWeight: 500, background: "transparent", border: "1px solid rgba(55,201,154,.5)", color: "#37c99a", whiteSpace: "nowrap" }}><Globe size={11} strokeWidth={2.2} />Public</span>
        <span style={{ fontSize: 10, color: "#71717c" }}>Tous les utilisateurs</span>
      </div>
    );
  }
  // iter148 — accès = UNION « Accès total » (Premier Admin + administrateurs) ∪ « Utilisateurs autorisés » (explicites), dédupliqué.
  const admins = (users || []).filter((u) => !u.is_deleted && (u.is_first_admin || u.role === "admin"));
  const explicit = (t.authorized_user_ids || []).map((uid) => users.find((u) => u.id === uid)).filter((u) => u && !admins.some((a) => a.id === u.id));
  const auth = [...admins, ...explicit];
  const roleOf = (u) => (u.is_first_admin ? "Premier Admin" : u.role === "admin" ? "Administrateur" : "Autorisé");
  if (auth.length === 0) {
    return (
      <div className="flex items-center" data-testid={`doctype-access-pill-${t.id}`} data-state="restricted-empty">
        <span className="inline-flex items-center" style={{ gap: 5, borderRadius: 100, padding: "3px 10px", fontSize: 10, background: "rgba(220,38,38,.08)", border: "0.5px solid rgba(220,38,38,.35)", color: "#f4a4a4", whiteSpace: "nowrap" }} data-testid={`doctype-access-empty-${t.id}`}>
          <AlertTriangle size={11} style={{ color: "#DC2626" }} />Aucun utilisateur autorisé
        </span>
      </div>
    );
  }
  const shown = auth.slice(0, 3);
  const extra = auth.length - shown.length;
  const nameOf = (u) => `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email;
  return (
    <div className="flex items-center relative" style={{ gap: 9 }} data-testid={`doctype-access-pill-${t.id}`} data-state="restricted" onMouseEnter={() => setPop(true)} onMouseLeave={() => setPop(false)}>
      <button type="button" onClick={onOpenList} className="flex items-center" style={{ padding: 0, background: "transparent", border: "none", cursor: "pointer" }} title="Modifier les utilisateurs autorisés" data-testid={`doctype-access-avatars-${t.id}`}>
        {shown.map((u, i) => (
          <span key={u.id} title={`${nameOf(u)} · ${roleOf(u)}`} className="inline-flex rounded-full" style={{ marginLeft: i === 0 ? 0 : -6, zIndex: 10 - i, border: "1.5px solid #161619", borderRadius: "50%", position: "relative" }} data-testid={`doctype-access-avatar-${t.id}-${u.id}`}>
            <UserAvatar user={u} size={24} />
          </span>
        ))}
        {extra > 0 && <span className="inline-flex items-center justify-center rounded-full" style={{ width: 24, height: 24, marginLeft: -6, zIndex: 1, background: "rgba(255,255,255,.08)", color: "#a4a4ae", fontSize: 9, fontWeight: 500, border: "1.5px solid #161619", position: "relative" }} data-testid={`doctype-access-extra-${t.id}`}>+{extra}</span>}
      </button>
      <span style={{ fontSize: 10, color: "#71717c", whiteSpace: "nowrap" }} data-testid={`doctype-access-count-${t.id}`}><b style={{ color: "#a4a4ae", fontWeight: 500 }}>{auth.length}</b> utilisateur{auth.length > 1 ? "s" : ""} autorisé{auth.length > 1 ? "s" : ""}</span>
      {pop && (
        <div className="absolute left-0" style={{ top: "calc(100% + 6px)", zIndex: 40, minWidth: 220, background: "#18181c", border: "0.5px solid rgba(255,255,255,.12)", borderRadius: 8, padding: 6, boxShadow: "0 10px 28px rgba(0,0,0,.5)" }} data-testid={`doctype-access-popover-${t.id}`}>
          {[["Accès total", admins, "total"], ["Utilisateurs autorisés", explicit, "explicit"]].filter(([, l]) => l.length).map(([label, list, k]) => (
            <div key={k} data-testid={`doctype-access-popover-${t.id}-${k}`}>
              <div style={{ fontSize: 9, letterSpacing: ".6px", textTransform: "uppercase", color: "#71717c", fontWeight: 500, padding: "4px 6px 2px" }}>{label} ({list.length})</div>
              {list.map((u) => <div key={u.id} className="flex items-center" style={{ gap: 8, padding: "4px 6px", fontSize: 11, color: "#ececf0" }}><UserAvatar user={u} size={20} /><span style={{ flex: 1 }}>{nameOf(u)}</span><span style={{ fontSize: 9, color: "#71717c" }}>{roleOf(u)}</span></div>)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function DocumentTypesAdmin() {
  const goBack = useGoBack("/documents");
  const navigate = useNavigate();
  const { user } = useAuth();
  const [types, setTypes] = useState([]);
  const [docsCounts, setDocsCounts] = useState({});
  const [responsablesByType, setResponsablesByType] = useState({}); // { type_id: [user_id] }
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  // Spec rev122 (2026-02) — Restriction dialog state.
  const [restrictOpen, setRestrictOpen] = useState(false);
  const [restrictTarget, setRestrictTarget] = useState(null); // { kind, id, label }

  // Spec rev69 (2026-08-02) — tri cliquable sur les en-têtes Type / Docs / Mode,
  // cohérent avec les autres tableaux (Documents, Tâches) via ColumnHeaderMenu.
  const [sortKey, setSortKey] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const doSort = (key, dir) => { setSortKey(key); setSortDir(dir); };

  // Spec rev70 (2026-08-02) — champ de recherche instantanée par nom.
  const [search, setSearch] = useState("");
  // Spec iter164s (2026-02-25) — state pour la modale rétablie.
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [restorePivotOpen, setRestorePivotOpen] = useState(false);

  // Spec rev109 (2026-02) — filtres au-dessus du tableau :
  //   • categoryFilter : id de la catégorie ou "" (toutes).
  //   • modeFilter     : "all" | "stepped" | "simple".
  const [categoryFilter, setCategoryFilter] = useState("");
  const [modeFilter, setModeFilter] = useState("all");

  // Spec rev94 (2026-08-02) — état du recalcul en cours (par type_id).
  const [recomputingId, setRecomputingId] = useState(null);
  const recomputeDeadlines = async (t) => {
    setRecomputingId(t.id);
    try {
      const { data } = await api.post(`/document-types/${t.id}/recompute-deadlines`);
      const n = data?.updated_docs ?? 0;
      const r = data?.recomputed_deadlines ?? 0;
      if (n === 0) {
        toast.info("Rien à recalculer", { description: `Aucune échéance n'a pu être matérialisée pour « ${t.name} »` });
      } else {
        toast.success(`${n} document${n > 1 ? "s" : ""} mis à jour`, {
          description: `${r} échéance${r > 1 ? "s" : ""} recalculée${r > 1 ? "s" : ""} pour « ${t.name} »`,
        });
      }
    } catch (e) {
      toast.error("Recalcul impossible", { description: e.response?.data?.detail || "Erreur inattendue." });
    } finally {
      setRecomputingId(null);
    }
  };

  const isAdmin = user?.role === "admin";

  const load = async () => {
    setLoading(true);
    try {
      // Spec rev74 (2026-08-02) — fetch en parallèle : types + docs counts +
      // matrice by-type (viewers) + liste des utilisateurs (pour peupler le
      // popover Responsables). Le popover fait ensuite un appel dédié
      // by-type/{id}/entries à son ouverture pour récupérer les actions
      // détaillées.
      const [tRes, cRes, vRes, uRes, catRes] = await Promise.all([
        api.get("/document-types"),
        api.get("/document-types/counts"),
        api.get("/user-type-permissions/by-type/viewers"),
        api.get("/users"),
        api.get("/document-type-categories"),
      ]);
      setTypes(tRes.data || []);
      setDocsCounts(cRes.data || {});
      setResponsablesByType(vRes.data || {});
      setUsers((uRes.data || []).filter((u) => !u.is_deleted));
      setCategories(catRes.data || []);
    } catch (err) {
      toast.error("Impossible de charger les types", { description: err.response?.data?.detail || "" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Spec rev69 — dérive `sortedTypes` selon `sortKey` + `sortDir`.
  //   • name : tri alpha localeCompare('fr').
  //   • docs : par nombre de documents (fallback 0).
  //   • mode : regroupe séquentiels & non-séquentiels ensemble
  //     (sequential=0, parallel=1) — le tri secondaire par nom garantit
  //     un ordre stable à l'intérieur de chaque groupe.
  const sortedTypes = useMemo(() => {
    const q = search.trim().toLowerCase();
    // Spec rev70 — filtre par nom avant tri.
    let filtered = q
      ? types.filter((t) => (t.name || "").toLowerCase().includes(q))
      : types;
    // Spec rev109 — filtres Catégorie + Mode.
    if (categoryFilter) {
      filtered = filtered.filter((t) => t.category_id === categoryFilter);
    }
    if (modeFilter === "stepped") {
      filtered = filtered.filter((t) => (t.workflow_mode || "sequential") !== "simple");
    } else if (modeFilter === "simple") {
      filtered = filtered.filter((t) => (t.workflow_mode || "sequential") === "simple");
    }
    const arr = [...filtered];
    const dir = sortDir === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      const nameA = (a.name || "").toLowerCase();
      const nameB = (b.name || "").toLowerCase();
      if (sortKey === "docs") {
        const ca = docsCounts[a.id] ?? 0;
        const cb = docsCounts[b.id] ?? 0;
        if (ca !== cb) return (ca - cb) * dir;
        return nameA.localeCompare(nameB, "fr");
      }
      if (sortKey === "mode") {
        const ma = (a.workflow_mode || "sequential") === "sequential" ? 0 : 1;
        const mb = (b.workflow_mode || "sequential") === "sequential" ? 0 : 1;
        if (ma !== mb) return (ma - mb) * dir;
        return nameA.localeCompare(nameB, "fr");
      }
      // name (default)
      return nameA.localeCompare(nameB, "fr") * dir;
    });
    return arr;
  }, [types, docsCounts, sortKey, sortDir, search, categoryFilter, modeFilter]);

  // Spec rev109 (2026-02) — duplication d'un type via l'API.
  // Spec rev142 (2026-02-06) — la duplication passe désormais par une
  // modale de confirmation & renommage (voir `DuplicateTypeDialog`).
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [duplicateTarget, setDuplicateTarget] = useState(null);
  const openDuplicate = (t) => {
    setDuplicateTarget(t);
    setDuplicateOpen(true);
  };
  const onDuplicated = async (created) => {
    await load();
    // Ouvre directement le dialog en mode édition sur la copie fraîche.
    setEditing(created);
    setOpen(true);
  };

  const del = async (t) => {
    // Spec rev144 (2026-02-06) — Remplacement du window.confirm() par la
    // modale DeleteTypeDialog. Le flux de suppression est piloté par
    // l'état ci-dessous.
    setDeleteTarget(t);
    setDeleteOpen(true);
  };

  // Spec rev144 (2026-02-06) — Modale de suppression rouge (remplace
  // window.confirm). L'appel API et le refresh sont pilotés par le
  // composant DeleteTypeDialog, on ne fait que rafraîchir ici.
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  if (!isAdmin) {
    return (
      <div className="p-6 text-center">
        <p className="text-muted-foreground">Cette page est réservée aux administrateurs.</p>
      </div>
    );
  }

  return (
    <div data-testid="doctypes-page" className="space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={goBack} className="gap-1" data-testid="back-to-documents">
          <ArrowLeft size={14} /> Documents
        </Button>
      </div>

      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold mb-1">Types de documents</h1>
          <p className="text-muted-foreground text-sm">
            Créez les types de documents utilisés dans Le Desk et définissez librement les champs à saisir pour chacun.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => { setEditing(null); setOpen(true); }}
            className="text-white gap-1.5 border-0"
            style={{ background: "#2B579A" }}
            data-testid="doctype-new"
          >
            <Plus size={14} /> Nouveau type
          </Button>
          <div className="shrink-0" style={{ width: 1, height: 26, background: "#333", marginLeft: 4 }} aria-hidden="true" />
          <HeaderProfileGroup />
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">Chargement…</div>
      ) : types.length === 0 ? (
        <Card className="bg-card border-[#3A3A3A] p-8 text-center">
          <div className="w-14 h-14 rounded-lg mx-auto mb-3 flex items-center justify-center" style={{ background: "#2B579A22", color: "#2B579A" }}>
            <FileText size={24} />
          </div>
          <h3 className="text-lg font-semibold mb-1">Aucun type pour l&apos;instant</h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            Créez votre premier type pour commencer (ex : Chèque reçu, Acte VSP, Contrat de réservation…).
          </p>
        </Card>
      ) : (
        <>
          {/* Spec rev76 (2026-08-02) — panneau de gestion des catégories
              (renommer / supprimer). La création reste dans le dialog du
              Type via l'option « + Créer une nouvelle catégorie… ». */}
          <CategoriesManagerPanel
            categories={categories}
            types={types}
            users={users}
            onChanged={load}
            isFirstAdmin={!!user?.is_first_admin}
          />
          {/* Spec rev70 (2026-08-02) — champ de recherche instantanée par nom
              de type. Style et data-testid alignés sur Documents / Tâches. */}
          <Card className="p-3 bg-card border-vertec-borderDark" data-testid="doctypes-filters">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative flex-1 min-w-[240px]">
                <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Rechercher un type par nom…"
                  className="w-full rounded-md focus:outline-none focus:ring-1 focus:ring-vertec-blue"
                  style={{
                    height: 32,
                    paddingLeft: 30, paddingRight: 28,
                    background: "#232326",
                    border: "1px solid #2A2A2A",
                    color: "#EDEDED",
                    fontSize: 12,
                  }}
                  data-testid="doctypes-search"
                />
                <SearchClearButton value={search} onClear={() => setSearch("")} testid="doctypes-search-clear" />
              </div>

              {/* Spec rev109 point 1 — Dropdown Catégorie. Liste scalable
                  (pas des puces) pour supporter un nombre croissant de
                  catégories sans overflow. */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1.5 rounded-md text-[12px] font-medium"
                    style={{
                      height: 32,
                      padding: "0 10px",
                      background: categoryFilter ? "rgba(43,87,154,0.14)" : "#232326",
                      border: `1px solid ${categoryFilter ? "rgba(43,87,154,0.45)" : "#2A2A2A"}`,
                      color: categoryFilter ? "#5A9EE0" : "#EDEDED",
                    }}
                    data-testid="doctypes-filter-category"
                  >
                    <span className="text-[11px] text-muted-foreground">Catégorie&nbsp;:</span>
                    <span>{categoryFilter
                      ? (categories.find((c) => c.id === categoryFilter)?.name || "—")
                      : "Toutes"}</span>
                    <ChevronDown size={12} className="opacity-70" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="min-w-[220px] max-h-[300px] overflow-y-auto">
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-widest">
                    Filtrer par catégorie
                  </DropdownMenuLabel>
                  <DropdownMenuItem
                    onClick={() => setCategoryFilter("")}
                    className="gap-2 cursor-pointer text-[12px]"
                    data-testid="doctypes-filter-category-all"
                  >
                    <span className="w-2 h-2 rounded-full shrink-0"
                          style={{ background: !categoryFilter ? "#22C55E" : "rgba(255,255,255,0.10)" }} />
                    <span className="flex-1">Toutes</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {categories.map((c) => (
                    <DropdownMenuItem
                      key={c.id}
                      onClick={() => setCategoryFilter(c.id)}
                      className="gap-2 cursor-pointer text-[12px]"
                      data-testid={`doctypes-filter-category-${c.id}`}
                    >
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: c.color }} />
                      <span className="flex-1">{c.name}</span>
                      {categoryFilter === c.id && <span style={{ color: c.color }}>●</span>}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Spec rev109 point 1 — Puces "Toutes / Avec étapes / Sans étape".
                  Le libellé change de "3 puces" à 3 boutons visuellement
                  cohérents avec le segmented control du panneau notifs. */}
              <div
                className="inline-flex items-center rounded-md p-0.5"
                style={{ background: "#111", border: "1px solid #2A2A2A" }}
                data-testid="doctypes-filter-mode"
              >
                {[
                  { key: "all",     label: "Toutes" },
                  { key: "stepped", label: "Avec étapes" },
                  { key: "simple",  label: "Sans étape" },
                ].map((m) => {
                  const active = modeFilter === m.key;
                  return (
                    <button
                      key={m.key}
                      type="button"
                      onClick={() => setModeFilter(m.key)}
                      className="px-2.5 py-1 rounded text-[11px] font-medium transition-colors"
                      style={{
                        background: active ? "#0078D4" : "transparent",
                        color: active ? "#FFFFFF" : "rgba(255,255,255,0.55)",
                      }}
                      data-testid={`doctypes-filter-mode-${m.key}`}
                    >
                      {m.label}
                    </button>
                  );
                })}
              </div>

              <span className="text-[11px] text-muted-foreground tabular-nums ml-auto" data-testid="doctypes-search-count">
                {(search.trim() || categoryFilter || modeFilter !== "all")
                  ? `${sortedTypes.length} / ${types.length} type${types.length > 1 ? "s" : ""}`
                  : `${types.length} type${types.length > 1 ? "s" : ""}`}
              </span>

              {/* Spec iter164s (2026-02-25) — bouton admin RÉTABLI.
                  Ouvre la modale de diagnostic (dry-run + heuristique)
                  puis apply avec backup automatique. Réservé Premier
                  Admin. */}
              {/* Spec iter164M (2026-02-27) — chantiers Nettoyage & Restauration
                  terminés + validés. Boutons MASQUÉS mais code conservé.
                  Décommenter pour ré-activer si besoin. */}
              {false && user?.is_first_admin && (
                <button
                  type="button"
                  onClick={() => setCleanupOpen(true)}
                  className="inline-flex items-center gap-1.5 rounded-md text-[11px] font-semibold px-2.5 py-1"
                  style={{
                    height: 32,
                    background: "rgba(220,38,38,0.10)",
                    border: "1px solid rgba(220,38,38,0.35)",
                    color: "#F87171",
                  }}
                  data-testid="doctypes-cleanup-propagation-btn"
                  title="Diagnostiquer et corriger les étapes propagées par erreur sur les documents existants (backup automatique)"
                >
                  <ShieldCheck size={12} strokeWidth={2.2} /> Nettoyer les propagations parasites
                </button>
              )}

              {false && user?.is_first_admin && (
                <button
                  type="button"
                  onClick={() => setRestorePivotOpen(true)}
                  className="inline-flex items-center gap-1.5 rounded-md text-[11px] font-semibold px-2.5 py-1"
                  style={{
                    height: 32,
                    background: "rgba(234,88,12,0.10)",
                    border: "1px solid rgba(234,88,12,0.40)",
                    color: "#FB923C",
                  }}
                  data-testid="doctypes-restore-pivot-btn"
                  title="Restaurer VSP + Final à leur état du 23/08/2026 (sauf DC0222 et DC0223)"
                >
                  <ShieldCheck size={12} strokeWidth={2.2} /> Restauration 23/08 (VSP + Final)
                </button>
              )}
            </div>
          </Card>
          <div className="rounded-md border border-vertec-borderDark mt-3 doctypes-table-scroll" data-testid="doctypes-table-scroll">
          <table className="w-full text-sm" data-testid="doctypes-table">
            <thead>
              <tr className="text-left bg-[#242424] border-b border-white/10">
                {/* Spec 2026-02-XX rev29 — colonne « Nom complet » retirée
                    (redondante avec le badge). L'édition inline du nom court
                    (crayon rapide + regen + save/cancel) est également
                    supprimée : l'édition passe désormais UNIQUEMENT par le
                    dialog complet via l'icône Modifier de la colonne Actions.
                    Spec rev69 (2026-08-02) — tri cliquable sur Type / Docs /
                    Mode via <ColumnHeaderMenu> (cohérent avec Documents / Tâches). */}
                <th className="px-3 py-2 text-[11px] uppercase tracking-widest text-muted-foreground">
                  <ColumnHeaderMenu
                    label="Type"
                    options={null}
                    current=""
                    onSort={(dir) => doSort("name", dir)}
                    sortActive={sortKey === "name"}
                    sortDir={sortDir}
                    testId="doctypes-name"
                    accent="#5A9EE0"
                  />
                </th>
                {/* Spec rev75 (2026-08-02) — nouvelle colonne CATÉGORIE.
                    Badge de la catégorie transversale, vide si aucune. */}
                <th className="px-3 py-2 text-[11px] uppercase tracking-widest text-muted-foreground whitespace-nowrap">
                  Catégorie
                </th>
                <th className="px-3 py-2 text-[11px] uppercase tracking-widest text-muted-foreground">Étapes</th>
                {/* Spec rev68 (2026-08-02) — nouvelles colonnes DOCS + MODE. */}
                <th className="px-3 py-2 text-[11px] uppercase tracking-widest text-muted-foreground text-center whitespace-nowrap">
                  <div className="inline-flex">
                    <ColumnHeaderMenu
                      label="Docs"
                      options={null}
                      current=""
                      onSort={(dir) => doSort("docs", dir)}
                      sortActive={sortKey === "docs"}
                      sortDir={sortDir}
                      testId="doctypes-docs"
                      accent="#5A9EE0"
                    />
                  </div>
                </th>
                <th className="px-3 py-2 text-[11px] uppercase tracking-widest text-muted-foreground whitespace-nowrap">
                  <ColumnHeaderMenu
                    label="Mode"
                    options={null}
                    current=""
                    onSort={(dir) => doSort("mode", dir)}
                    sortActive={sortKey === "mode"}
                    sortDir={sortDir}
                    testId="doctypes-mode"
                    accent="#5A9EE0"
                  />
                </th>
                {/* Spec rev74 (2026-08-02) — nouvelle colonne « Responsables ».
                    Point d'entrée UNIQUE pour piloter la matrice user × type
                    (remplace l'accès depuis Paramètres > Utilisateurs). */}
                <th className="px-3 py-2 text-[11px] uppercase tracking-widest text-muted-foreground whitespace-nowrap">
                  Responsables
                </th>
                {/* Spec rev142 (2026-02-06) — nouvelle colonne « Accès » :
                    montre le statut de restriction du type (public vs
                    restreint · N). Réservée au Premier Admin (les autres
                    admins voient la colonne mais l'action Restreindre est
                    cachée — Rev138). */}
                <th className="px-3 py-2 text-[11px] uppercase tracking-widest text-muted-foreground whitespace-nowrap">
                  Accès
                </th>
                <th className="px-3 py-2 text-[11px] uppercase tracking-widest text-muted-foreground text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedTypes.map((t) => {
                const isSeq = (t.workflow_mode || "sequential") === "sequential";
                const docsCount = docsCounts[t.id] ?? 0;
                return (
                <tr key={t.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`doctype-row-${t.id}`}>
                  <td className="px-3 py-2">
                    <div className="inline-flex items-center gap-2">
                      <span
                        data-testid={`doctype-badge-${t.id}`}
                        className="inline-flex items-center text-[11px] font-semibold uppercase px-3 py-1 rounded-full whitespace-nowrap"
                        style={{
                          background: `${(t.color || "#666")}1F`,
                          border: `1px solid ${(t.color || "#666")}55`,
                          color: t.color || "#9CA3AF",
                          letterSpacing: 0,
                        }}
                        title={t.name}
                      >
                        {t.name || "—"}
                      </span>
                      {/* Spec rev88/rev91 (2026-08-02) — badge d'alerte pour
                          les types dont ≥1 étape a une échéance non
                          réellement calculable. Trois cas couverts :
                            (a) mode "fixed" sans deadline_fixed renseigné
                            (b) mode "from_step" sans ancre valide
                            (c) délai marqué non-obligatoire alors qu'un
                                mode/durée est renseigné (config incohérente
                                — les valeurs visibles ne produiront jamais
                                d'échéance). */}
                      {(() => {
                        const brokenSteps = (t.workflow_steps || []).map((s) => {
                          const mode = s.deadline_mode;
                          let reason = null;
                          if (mode === "fixed" && !s.deadline_fixed) {
                            reason = "Mode « date fixe » sans date renseignée";
                          } else if (mode === "from_step" && !s.deadline_from_step_id) {
                            reason = "Mode « depuis une étape » sans ancre";
                          } else if (
                            s.deadline_required === false &&
                            (mode || (s.deadline_days && s.deadline_days > 0) ||
                             s.deadline_fixed || s.deadline_from_step_id)
                          ) {
                            reason = "Délai optionnel avec valeurs renseignées (aucune échéance ne sera calculée)";
                          }
                          return reason ? { label: s.label || "Étape sans nom", reason } : null;
                        }).filter(Boolean);
                        if (brokenSteps.length === 0) return null;
                        // Regroupe par raison pour un tooltip plus lisible.
                        const byReason = {};
                        for (const b of brokenSteps) {
                          if (!byReason[b.reason]) byReason[b.reason] = [];
                          byReason[b.reason].push(b.label);
                        }
                        return (
                          <TooltipProvider delayDuration={200}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span
                                  className="inline-flex items-center justify-center rounded-full cursor-help"
                                  style={{
                                    width: 20, height: 20,
                                    background: "rgba(234,88,12,0.14)",
                                    border: "1px solid rgba(234,88,12,0.45)",
                                    color: "#EA580C",
                                  }}
                                  data-testid={`doctype-config-warn-${t.id}`}
                                  aria-label="Configuration incohérente"
                                >
                                  <AlertTriangle size={11} strokeWidth={2.4} />
                                </span>
                              </TooltipTrigger>
                              <TooltipContent
                                side="right"
                                sideOffset={8}
                                className="max-w-[320px] text-[11px] leading-snug"
                                style={{ background: "#1E1E1E", color: "#EDEDED", border: "1px solid #2A2A2A" }}
                              >
                                <div className="font-semibold text-[#EA580C] mb-1.5">
                                  Config d'échéance incohérente
                                </div>
                                <div className="space-y-2">
                                  {Object.entries(byReason).map(([reason, labels]) => (
                                    <div key={reason}>
                                      <div className="text-muted-foreground mb-0.5">{reason} :</div>
                                      <ul className="list-disc pl-4 space-y-0.5">
                                        {labels.map((n, i) => (
                                          <li key={i} className="text-white">{n}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  ))}
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        );
                      })()}
                      {/* Spec rev94 (2026-08-02) — bouton « Recalculer » à
                          côté du badge d'alerte : déclenche l'endpoint
                          bulk POST /api/document-types/{id}/recompute-
                          deadlines. Visible uniquement quand le badge
                          d'alerte est présent (types avec ≥1 étape
                          incohérente) → contextuel + moins de bruit. La
                          page entière est déjà admin-gated (isAdmin) au
                          top-level, aucun besoin de re-checker ici. */}
                      {(() => {
                        const brokenSteps = (t.workflow_steps || []).filter((s) => {
                          const mode = s.deadline_mode;
                          if (mode === "fixed" && !s.deadline_fixed) return true;
                          if (mode === "from_step" && !s.deadline_from_step_id) return true;
                          if (s.deadline_required === false && (mode || (s.deadline_days && s.deadline_days > 0) || s.deadline_fixed || s.deadline_from_step_id)) return true;
                          return false;
                        });
                        if (brokenSteps.length === 0) return null;
                        const busy = recomputingId === t.id;
                        return (
                          <TooltipProvider delayDuration={200}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  disabled={busy}
                                  onClick={(e) => { e.stopPropagation(); recomputeDeadlines(t); }}
                                  className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full transition-colors disabled:opacity-40"
                                  style={{
                                    background: "rgba(90,158,224,0.14)",
                                    color: "#5A9EE0",
                                    border: "1px solid rgba(90,158,224,0.28)",
                                    letterSpacing: 0.2,
                                  }}
                                  data-testid={`doctype-recompute-${t.id}`}
                                  aria-label="Recalculer les échéances des documents existants"
                                >
                                  <RotateCw size={10} strokeWidth={2.4} className={busy ? "animate-spin" : ""} />
                                  {busy ? "Recalcul…" : "Recalculer"}
                                </button>
                              </TooltipTrigger>
                              <TooltipContent
                                side="right"
                                sideOffset={6}
                                className="max-w-[280px] text-[11px] leading-snug"
                                style={{ background: "#1E1E1E", color: "#EDEDED", border: "1px solid #2A2A2A" }}
                              >
                                Applique la config actuelle du type aux documents existants (matérialise les échéances manquantes sans écraser celles déjà calculées).
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        );
                      })()}
                    </div>
                  </td>
                  {/* --- CATÉGORIE (rev75) --- */}
                  <td className="px-3 py-2" data-testid={`doctype-category-${t.id}`}>
                    {(() => {
                      const cat = categories.find((c) => c.id === t.category_id);
                      if (!cat) {
                        return <span className="text-[11px] italic text-muted-foreground">—</span>;
                      }
                      return (
                        <span
                          className="inline-flex items-center text-[10px] font-semibold uppercase px-2.5 py-0.5 rounded-full whitespace-nowrap"
                          style={{
                            background: `${cat.color}1F`,
                            border: `1px solid ${cat.color}55`,
                            color: cat.color,
                            letterSpacing: 0.3,
                          }}
                          title={cat.name}
                        >
                          {cat.name}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-3 py-2 text-xs" data-testid={`doctype-steps-${t.id}`}>
                    {/* Spec rev109 point 2 + 3 — chips numérotées séparées
                        par une flèche compacte, OU indicateur "Document
                        direct" pour les types workflow_mode="simple". */}
                    {(t.workflow_mode || "sequential") === "simple" ? (
                      <span
                        className="inline-flex items-center gap-1.5 text-[11.5px] font-medium"
                        style={{ color: "#22C55E" }}
                        data-testid={`doctype-steps-simple-${t.id}`}
                      >
                        <Zap size={13} strokeWidth={2.4} />
                        Document direct (sans workflow)
                      </span>
                    ) : (t.workflow_steps || []).length === 0 ? (
                      <span className="italic text-muted-foreground">Aucune étape</span>
                    ) : (
                      <div className="flex flex-wrap items-center gap-y-1" data-testid={`doctype-steps-chips-${t.id}`}>
                        {(t.workflow_steps || []).map((s, i) => {
                          // Spec rev114 (2026-02) — astérisque rouge si
                          // l'étape porte au moins un champ requis (list
                          // canonique OR triplet legacy actif).
                          const isRequired =
                            (Array.isArray(s.required_fields) && s.required_fields.length > 0) ||
                            !!s.required_field_active;
                          return (
                          <span key={s.id || i} className="inline-flex items-center">
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium whitespace-nowrap"
                              style={{
                                background: "rgba(43,87,154,0.10)",
                                border: "1px solid rgba(43,87,154,0.35)",
                                color: "#B0C8E5",
                              }}
                              title={s.label + (isRequired ? " · champ requis" : "")}
                            >
                              <span className="tabular-nums font-bold" style={{ color: "#5A9EE0" }}>{i + 1}</span>
                              <span aria-hidden="true" style={{ color: "rgba(255,255,255,0.20)" }}>·</span>
                              <span className="max-w-[180px] truncate">{s.label || "Étape sans nom"}</span>
                              {isRequired && (
                                <span
                                  aria-label="Étape obligatoire"
                                  style={{ color: "#EF4444", fontWeight: 700, marginLeft: 1 }}
                                  data-testid={`doctype-step-required-${t.id}-${i}`}
                                >
                                  *
                                </span>
                              )}
                            </span>
                            {i < (t.workflow_steps || []).length - 1 && (
                              <ArrowRight size={11} className="mx-1 shrink-0 opacity-50" />
                            )}
                          </span>
                          );
                        })}
                      </div>
                    )}
                  </td>
                  {/* --- DOCS (rev68) — clickable link to Documents filtered
                       by this type (rev109 point 4). --- */}
                  <td className="px-3 py-2 text-center text-xs tabular-nums" data-testid={`doctype-docs-count-${t.id}`}>
                    {docsCount > 0 ? (
                      <button
                        type="button"
                        onClick={() => navigate(`/documents?type_id=${t.id}`)}
                        className="inline-flex items-center justify-center rounded-full font-semibold transition-colors hover:brightness-110"
                        style={{
                          minWidth: 26, height: 22, padding: "0 8px",
                          background: "rgba(0,120,212,0.14)",
                          color: "#0078D4",
                          border: "1px solid rgba(0,120,212,0.40)",
                          textDecoration: "underline",
                          textUnderlineOffset: 2,
                        }}
                        title={`Ouvrir les ${docsCount} document(s) de ce type`}
                        data-testid={`doctype-docs-count-link-${t.id}`}
                      >
                        {docsCount}
                      </button>
                    ) : (
                      <span
                        className="inline-flex items-center justify-center rounded-full font-semibold"
                        style={{
                          minWidth: 26, height: 22, padding: "0 8px",
                          background: "rgba(107,114,128,0.10)",
                          color: "#6B7280",
                          border: "1px solid rgba(107,114,128,0.25)",
                        }}
                        title="Aucun document"
                      >
                        0
                      </span>
                    )}
                  </td>
                  {/* --- MODE (rev68) --- */}
                  <td className="px-3 py-2" data-testid={`doctype-mode-${t.id}`}>
                    <span
                      className="inline-flex items-center gap-1.5 text-[11px] font-medium whitespace-nowrap"
                      style={{ color: isSeq ? "#5A9EE0" : "#9CA3AF" }}
                      title={isSeq
                        ? "Workflow séquentiel — étapes exécutées dans l'ordre"
                        : "Workflow non-séquentiel (parallèle) — étapes libres"}
                    >
                      {isSeq
                        ? <ArrowRight size={13} strokeWidth={2.4} />
                        : <Shuffle size={13} strokeWidth={2.2} />}
                      {isSeq ? "Séquentiel" : "Non-séquentiel"}
                    </span>
                  </td>
                  {/* --- RESPONSABLES (rev74) --- avatars empilés + « Gérer »
                       ou état vide « Aucun · Assigner ». Le clic ouvre le
                       DocumentTypeResponsablesPopover (unique point d'entrée
                       pour la matrice user × type). */}
                  <td className="px-3 py-2" data-testid={`doctype-resp-${t.id}`}>
                    {(() => {
                      const respIds = responsablesByType[t.id] || [];
                      const respUsers = respIds
                        .map((uid) => users.find((u) => u.id === uid))
                        .filter(Boolean);
                      const shown = respUsers.slice(0, 4);
                      const extra = respUsers.length - shown.length;
                      const trigger = respUsers.length > 0 ? (
                        <button
                          type="button"
                          className="inline-flex items-center gap-2 group"
                          data-testid={`doctype-resp-manage-${t.id}`}
                        >
                          <span className="flex" style={{ marginLeft: 4 }}>
                            {shown.map((u, i) => (
                              <span key={u.id} style={{ marginLeft: i === 0 ? -4 : -8, zIndex: 10 - i }}>
                                <AvatarBlock
                                  initials={`${u.first_name?.[0] || ""}${u.last_name?.[0] || ""}`}
                                  size={24}
                                  seed={u.id || u.email}
                                  name={`${u.first_name} ${u.last_name}`.trim()}
                                />
                              </span>
                            ))}
                            {extra > 0 && (
                              <span
                                className="inline-flex items-center justify-center rounded-full text-[10px] font-semibold"
                                style={{
                                  marginLeft: -8, zIndex: 5,
                                  width: 24, height: 24,
                                  background: "#242424",
                                  color: "#9CA3AF",
                                  border: "1px solid #3A3A3A",
                                }}
                                title={`+${extra} autre(s)`}
                              >+{extra}</span>
                            )}
                          </span>
                          {/* Spec rev142 (2026-02-06) — état « N assignés »
                              en vert avec coche, plus visuel que le
                              simple lien « Gérer ». */}
                          <span
                            className="inline-flex items-center gap-1 text-[11px] font-semibold group-hover:brightness-125"
                            style={{ color: "#4ADE80" }}
                            data-testid={`doctype-resp-count-${t.id}`}
                          >
                            <CheckCircle2 size={12} strokeWidth={2.4} />
                            {respUsers.length} assigné{respUsers.length > 1 ? "s" : ""}
                          </span>
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-white group"
                          data-testid={`doctype-resp-manage-${t.id}`}
                        >
                          {/* Spec rev109 point 5 — icône triangle orange
                              devant "Aucun" pour signaler l'absence de
                              responsables par défaut sur ce type. */}
                          <AlertTriangle
                            size={12}
                            strokeWidth={2.4}
                            style={{ color: "#F97316" }}
                            aria-label="Aucun responsable configuré"
                            data-testid={`doctype-resp-warn-${t.id}`}
                          />
                          <span className="italic">Aucun</span>
                          <span className="text-[#5A9EE0] underline underline-offset-2 group-hover:text-[#7DB6EE] not-italic">
                            · Assigner
                          </span>
                        </button>
                      );
                      return (
                        <DocumentTypeResponsablesPopover
                          type={t}
                          users={users}
                          onSaved={load}
                          triggerAsAvatars={trigger}
                        />
                      );
                    })()}
                  </td>
                  {/* Spec rev178 (2026-02) — cellule « Accès » enrichie :
                      • Non restreint → globe vert + « Public »
                      • Restreint     → cadenas orange + AVATARS empilés
                                        (+ badge « +N » si > 3 users)
                      Spec iter96 (Feb 2026) — les avatars affichés sont
                      désormais ceux des utilisateurs RÉELLEMENT autorisés
                      (`t.authorized_user_ids` fourni par le backend depuis
                      `user_type_permissions` view=True), et non plus les
                      responsables qui sont un concept distinct. */}
                  <td className="px-3 py-2" data-testid={`doctype-access-${t.id}`}>
                    <AccessCell
                      type={t}
                      users={users}
                      onOpenList={() => { setRestrictTarget({ kind: "type", id: t.id, label: t.name }); setRestrictOpen(true); }}
                    />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex gap-1">
                      <Button
                        size="icon" variant="ghost"
                        onClick={() => { setEditing(t); setOpen(true); }}
                        data-testid={`doctype-edit-${t.id}`}
                        className="h-8 w-8"
                        title="Modifier le type"
                      >
                        <Pencil size={13} />
                      </Button>
                      {/* Spec rev109 point 6 — Duplication du type via un
                          POST /api/document-types/{id}/duplicate qui crée
                          une copie « (copie N) » et ouvre le dialog
                          d'édition sur la copie fraîche. */}
                      <Button
                        size="icon" variant="ghost"
                        onClick={() => openDuplicate(t)}
                        data-testid={`doctype-duplicate-${t.id}`}
                        className="h-8 w-8 text-[#5A9EE0] hover:text-[#7DB6EE]"
                        title="Dupliquer ce type"
                      >
                        <Copy size={13} />
                      </Button>
                      {user?.is_first_admin && (
                      <Button
                        size="icon" variant="ghost"
                        onClick={() => { setRestrictTarget({ kind: "type", id: t.id, label: t.name }); setRestrictOpen(true); }}
                        data-testid={`doctype-restrict-${t.id}`}
                        className="h-8 w-8"
                        style={{ color: t.restriction_active ? "#F97316" : "#8B8F94" }}
                        title={t.restriction_active ? "Accès restreint — utilisateurs autorisés" : "Accès public — activer la restriction"}
                      >
                        {t.restriction_active ? <LockIcon size={13} /> : <ShieldCheck size={13} />}
                      </Button>
                      )}
                      <Button
                        size="icon" variant="ghost"
                        onClick={() => del(t)}
                        data-testid={`doctype-delete-${t.id}`}
                        className="h-8 w-8 text-red-400 hover:text-red-300"
                        title="Supprimer le type"
                      >
                        <Trash2 size={13} />
                      </Button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
          </div>
          {/* Empty-state pour recherche sans résultat */}
          {search.trim() && sortedTypes.length === 0 && (
            <p className="text-center text-xs text-muted-foreground italic py-6" data-testid="doctypes-search-empty">
              Aucun type ne correspond à « {search.trim()} ».
            </p>
          )}
        </>
      )}

      <DocumentTypeDialog
        open={open}
        onOpenChange={setOpen}
        editing={editing}
        onSaved={load}
      />

      {/* Spec rev122 (2026-02) — Restriction dialog partagé (types + catégories). */}
      <RestrictionDialog
        open={restrictOpen}
        onOpenChange={setRestrictOpen}
        kind={restrictTarget?.kind}
        id={restrictTarget?.id}
        label={restrictTarget?.label}
        users={users}
        onSaved={load}
      />

      {/* Spec rev142 (2026-02-06) — Duplication d'un type avec renommage
          obligatoire (modale de confirmation). */}
      <DuplicateTypeDialog
        open={duplicateOpen}
        onOpenChange={setDuplicateOpen}
        type={duplicateTarget}
        onDuplicated={onDuplicated}
      />

      {/* Spec rev144 (2026-02-06) — Suppression d'un type via modale
          rouge (remplace window.confirm). Affiche le nombre de docs
          impactés et un bandeau irréversible. */}
      <DeleteTypeDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        type={deleteTarget}
        docsCount={deleteTarget ? (docsCounts[deleteTarget.id] || 0) : 0}
        onDeleted={load}
      />

      {/* Spec iter164s (2026-02-25) — modale rétablie. Rendue seulement
          quand ouverte pour éviter le fetch dry-run inutile. */}
      {user?.is_first_admin && cleanupOpen && (
        <CleanupWrongPropagationDialog
          open={cleanupOpen}
          onOpenChange={setCleanupOpen}
        />
      )}

      {/* Spec iter164G (2026-02-26) — modale Restauration 23/08 dédiée. */}
      {user?.is_first_admin && restorePivotOpen && (
        <RestorePivotDialog
          open={restorePivotOpen}
          onOpenChange={setRestorePivotOpen}
        />
      )}
    </div>
  );
}
